"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useNotifications } from '@/context/NotificationContext';
import { useOrderSidebar } from '@/context/OrderSidebarContext';
import { useCheckout } from '@/context/CheckoutContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, X } from '@/components/icons';
import { cn, offcanvasSidebarPanelClass } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useScrollLock } from '@/hooks/useScrollLock';
import { Skeleton } from '@/components/ui/skeleton';
import { BrushCleaning } from 'lucide-react';
import { getProfileById } from '@/lib/supabase/profiles';
import {
  setActiveCartPreference,
  setActiveCartSnapshot,
  setActiveSharedCartExplicitByStore,
  setActiveSharedCartToken,
  setActiveSharedCartThemePriority,
  setActiveSharedCartRole,
  setActiveSharedCartGuestOwnerColor,
} from '@/lib/activeCartPreference';

const JACARANDA_HEX = '#a259ff';

export const NotificationsSidebar: React.FC = () => {
  const navigate = useNavigate();
  const { notifications, markAsRead, clearNotifications, loadingNotifications, sidebarOpen, closeSidebar, closeWithDelay, cancelCloseDelay } = useNotifications();
  const { openSidebar: openOrderSidebar } = useOrderSidebar();
  const { openSidebar: openCheckoutSidebar } = useCheckout();
  const [isClosing, setIsClosing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      closeSidebar();
      setIsClosing(false);
    }, 200);
  }, [closeSidebar]);

  // Pull-to-close: ref al ScrollArea
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Calculate if all notifications are read
  const allRead = notifications.length > 0 && notifications.every(n => n.is_read);
  const hasUnread = notifications.length > 0 && notifications.some(n => !n.is_read);

  const extractSharedTokenFromLink = useCallback((link: string): string | null => {
    if (!link) return null;
    try {
      const parsed = new URL(link, window.location.origin);
      return parsed.searchParams.get('shared_cart_token') || parsed.searchParams.get('share_token');
    } catch {
      const match = link.match(/[?&](shared_cart_token|share_token)=([^&#]+)/i);
      return match?.[2] ? decodeURIComponent(match[2]) : null;
    }
  }, []);

  const activateSharedCartFromNotification = useCallback(async (targetLink: string) => {
    // New architecture uses direct access instead of tokens
    // Notification links now point directly to store URLs instead of shared cart tokens
    // The checkout context will load shared carts based on user permissions
    try {
      navigate(targetLink);
    } catch (error) {
      console.error('[NotificationsSidebar] Error navigating to notification link:', error);
    }
  }, [navigate]);

  // Activar pull-to-close (solo en móvil)
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: sidebarOpen && !isDesktop
  });

  useScrollLock(sidebarOpen || isClosing, false, scrollAreaRef);

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    for (const notification of notifications) {
      if (!notification.is_read) {
        await markAsRead(notification.id);
      }
    }
  };

  // Recalcular medidas cuando el sidebar se abre
  useEffect(() => {
    if (sidebarOpen) {
      // Forzar recálculo inmediato
      recalculate();
      // Recalcular también después de múltiples delays para asegurar que el DOM esté actualizado
      const timeoutId1 = setTimeout(recalculate, 0);
      const timeoutId2 = setTimeout(recalculate, 50);
      const timeoutId3 = setTimeout(recalculate, 100);
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
      };
    }
  }, [sidebarOpen, recalculate]);

  // Click fuera para cerrar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if click is inside a portal (dialog, popover, menu, maps, etc.)
      const isPortal = target.closest('[data-radix-portal]') || 
                      target.closest('[data-radix-popper-content-wrapper]') ||
                      target.closest('[role="dialog"]') ||
                      target.closest('[role="menu"]') || 
                      target.closest('[role="listbox"]') ||
                      target.closest('.leaflet-container') ||
                      target.closest('.pac-container') ||
                      target.closest('[toast-close]') ||
                      target.closest('[data-radix-toast-announcer]');

      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(target) && !isPortal) {
        handleClose();
      }
    };

    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen, handleClose]);

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sidebarOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (sidebarOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sidebarOpen, handleClose]);

  useEffect(() => {
    // Animación de cierre al autocerrar con closeWithDelay
    if (!sidebarOpen && !isClosing) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
      }, 200);
    }
  }, [sidebarOpen, isClosing]);

  return (
    <>
      {/* Overlay */}
      {(sidebarOpen || isClosing) && (
        <div
          className={cn(
            'fixed inset-0 bg-transparent z-[9999] transition-opacity duration-200',
            isClosing && 'opacity-0 pointer-events-none'
          )}
          onClick={handleClose}
        />
      )}

      <div
      ref={sidebarRef}
      onMouseEnter={() => { if (isDesktop) cancelCloseDelay(); }}
      onMouseLeave={() => { if (isDesktop && sidebarOpen) closeWithDelay(1000); }}
      className={cn(
        'fixed right-0 w-full lg:w-[400px] bg-background z-[10000] flex flex-col transition-transform duration-200',
        offcanvasSidebarPanelClass(sidebarOpen, isClosing)
      )}
      style={{
        top: isDesktop ? `${headerHeight}px` : '0',
        bottom: !isDesktop ? 0 : undefined,
        height: isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh',
        pointerEvents: (sidebarOpen && !isClosing) ? 'auto' : 'none'
      }}
    >
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground min-h-[64px]">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Notificaciones
        </h2>
        <div className="flex items-center gap-2">
          {notifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (allRead) {
                  // If all are read, delete them
                  clearNotifications();
                } else {
                  // If there are unread, mark all as read
                  handleMarkAllAsRead();
                }
              }}
              className="h-8 text-xs text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <BrushCleaning className="w-4 h-4 mr-1" />
              {allRead ? 'Borrar' : 'Limpiar'}
            </Button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); handleClose(); }}
            className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1" ref={scrollAreaRef}>
        <div className="p-4 space-y-3">
          {loadingNotifications ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : notifications.length === 0 ? (
            <p className="text-muted-foreground text-center p-8 text-sm">
              No tienes notificaciones nuevas. ¡Mantente activo!
            </p>
          ) : (
            <div className="grid gap-3">
              {notifications.map((notification, index) => {
                const targetLink = (() => {
                  if (notification.link) {
                    try {
                      return notification.link
                        .replace(/\/orders\//, '/orden/')
                        .replace(/^\/tienda\/([^/?#]+)(.*)$/i, '/$1$2');
                    } catch (e) {
                      return notification.link;
                    }
                  }
                  if ((notification as any).order_id) return `/orden/${(notification as any).order_id}`;
                  if ((notification as any).meta && (notification as any).meta.order_id) return `/orden/${(notification as any).meta.order_id}`;
                  return notification.link;
                })();

                const handleClick = async () => {
                  if (!notification.is_read) {
                    markAsRead(notification.id);
                  }
                  
                  // Si es una notificación de subasta ganada, abrir OrderSidebar directamente
                  if (notification.type === 'auction_won' && (notification as any).reference_id) {
                    handleClose();
                    openOrderSidebar((notification as any).reference_id);
                    return;
                  }
                  
                  // Para notificaciones de canasto compartido: activar estado, navegar a la tienda y abrir sidebar
                  if (targetLink) {
                    const isSharedCartLink = /shared_cart_token=|share_token=/.test(targetLink);
                    if (isSharedCartLink) {
                      let preferredStoreId: string | undefined;
                      let storeSlug: string | undefined;
                      try {
                        const activation = await activateSharedCartFromNotification(targetLink);
                        preferredStoreId = activation?.storeId || undefined;
                        storeSlug = activation?.storeSlug || undefined;
                      } catch (error) {
                        console.error('[NotificationsSidebar] Error activating shared cart from notification:', error);
                      }
                      handleClose();
                      if (storeSlug) {
                        navigate(`/${storeSlug}`);
                        setTimeout(() => {
                          openCheckoutSidebar({ cartOnly: true, preferredStoreId });
                        }, 300);
                      } else {
                        openCheckoutSidebar({ cartOnly: true, preferredStoreId });
                      }
                      return;
                    }
                    handleClose();
                    navigate(targetLink);
                  }
                };

                return (
                  <div
                    key={notification.id}
                    onClick={handleClick}
                    className={cn(
                      'flex items-start p-3 rounded-md border transition-colors cursor-pointer',
                      notification.is_read ? 'bg-muted/20 border-muted' : 'bg-card border-primary/30',
                      'hover:bg-muted/30 focus:bg-muted/30',
                      'animate-in slide-in-from-bottom duration-200'
                    )}
                    style={{
                      animationDelay: `${index * 30}ms`,
                    }}
                  >
                    <Bell className={cn(
                      'h-4 w-4 mr-3 mt-1 flex-shrink-0',
                      notification.is_read ? 'text-muted-foreground' : 'text-primary'
                    )} />
                    <div className="flex-1 min-w-0">
                      {(notification as any).title && (
                        <p className={cn(
                          'text-sm font-semibold mb-0.5',
                          notification.is_read ? 'text-muted-foreground' : 'text-foreground'
                        )}>
                          {(notification as any).title}
                        </p>
                      )}
                      <p className={cn(
                        'text-sm',
                        notification.is_read ? 'text-muted-foreground' : (notification as any).title ? 'text-muted-foreground' : 'font-medium text-foreground'
                      )}>
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: es })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
    </>
  );
};
